import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Rating,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEnviarAvaliacaoMutation, useGetAvaliacaoQuery } from '../../api';
import { CabecalhoModal } from '../../components/cabecalho-modal';

/**
 * Avaliação do pedido como modal sobre o catálogo (rota filha do FundoCatalogo),
 * no mesmo padrão de produto/carrinho/pedido. A URL é a do link da notificação.
 */
export function AvaliacaoPage(): JSX.Element {
  const { idPedido: idStr } = useParams<{ idPedido: string }>();
  const navigate = useNavigate();
  const idPedido = idStr ?? '';

  function fechar(): void {
    if (window.history.length > 1) navigate(-1);
    else navigate('/catalogo');
  }
  const { data, isLoading, error } = useGetAvaliacaoQuery(idPedido, { skip: !idPedido });
  const [enviar, statusEnviar] = useEnviarAvaliacaoMutation();
  const [estrelas, setEstrelas] = useState<number | null>(null);
  const [comentario, setComentario] = useState('');
  const [enviado, setEnviado] = useState(false);

  // Avaliação é única por pedido e NÃO editável: se já existe, só mostramos.
  const jaAvaliado = !!data?.avaliacao;

  async function submeter(): Promise<void> {
    if (!estrelas) return;
    try {
      await enviar({ idPedido, estrelas, comentario: comentario.trim() || undefined }).unwrap();
      setEnviado(true);
    } catch {
      /* erro tratado pelo statusEnviar.error */
    }
  }

  function conteudo(): JSX.Element {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (error || !data) return <Alert severity="error">Pedido não encontrado</Alert>;
    if (enviado) return <Alert severity="success">Avaliação enviada. Obrigado pelo retorno!</Alert>;
    if (jaAvaliado) {
      return (
        <Stack spacing={2} alignItems="center">
          <Alert severity="info" sx={{ width: '100%' }}>
            Você já avaliou este pedido. Obrigado pelo retorno!
          </Alert>
          {data.avaliacao && (
            <>
              <Rating value={data.avaliacao.estrelas} readOnly size="large" />
              {data.avaliacao.comentario && (
                <Typography color="text.secondary" align="center">
                  “{data.avaliacao.comentario}”
                </Typography>
              )}
            </>
          )}
        </Stack>
      );
    }
    return (
      <Stack spacing={3}>
        <Typography color="text.secondary" align="center">
          Olá{data.pedido.clienteNome ? `, ${data.pedido.clienteNome}` : ''}! Como foi sua experiência com o pedido #{data.pedido.id.slice(0, 8)}?
        </Typography>
        <Box sx={{ textAlign: 'center' }}>
          <Rating value={estrelas} onChange={(_e, v) => setEstrelas(v)} size="large" />
        </Box>
        <TextField
          label="Comentário (opcional)"
          value={comentario}
          onChange={(e) => setComentario(e.target.value)}
          multiline
          minRows={3}
          fullWidth
        />
        {statusEnviar.error && (
          <Alert severity="error">Não foi possível enviar a avaliação. Tente novamente.</Alert>
        )}
        <Button variant="contained" size="large" onClick={submeter} disabled={!estrelas || statusEnviar.isLoading}>
          {statusEnviar.isLoading ? <CircularProgress size={20} /> : 'Enviar avaliação'}
        </Button>
      </Stack>
    );
  }

  return (
    <Dialog open onClose={fechar} fullWidth maxWidth="sm" scroll="paper">
      <CabecalhoModal titulo="Avalie seu pedido" aoFechar={fechar} />
      <DialogContent sx={{ py: 3 }}>{conteudo()}</DialogContent>
    </Dialog>
  );
}
